import React from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { BuildingOffice2Icon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { LinkedFixedAsset } from '../types/Document.ts';

export const UNSAVED_CHANGES_HINT = 'Sla eerst je wijzigingen op voordat je een vast actief koppelt.';

const CHIP_CLASS = 'inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';

type Props = {
    linkedFixedAsset: LinkedFixedAsset | null;
    hasUnsavedChanges: boolean;
    onAdd: () => void;
    onPreview: () => void;
    onUnlink: () => void;
};

export function BillFixedAssetChip({ linkedFixedAsset, hasUnsavedChanges, onAdd, onPreview, onUnlink }: Props) {
    if (!linkedFixedAsset) {
        return (
            <span title={hasUnsavedChanges ? UNSAVED_CHANGES_HINT : undefined} className="inline-flex">
                <button
                    type="button"
                    onClick={onAdd}
                    disabled={hasUnsavedChanges}
                    data-testid="add-fixed-asset-chip"
                    className={`${CHIP_CLASS} border border-dashed border-gray-400 bg-white text-gray-700 hover:border-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50`}
                >
                    <PlusIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    Vast actief
                </button>
            </span>
        );
    }

    return (
        <Popover className="inline-flex max-w-full">
            <PopoverButton
                data-testid="linked-fixed-asset-chip"
                className={`${CHIP_CLASS} border border-[#B7D9B7] bg-[#DBEDDB] text-[#1C3829] hover:bg-[#CFE6CF]`}
            >
                <BuildingOffice2Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">Activum · {linkedFixedAsset.name}</span>
                <ChevronDownIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            </PopoverButton>
            <PopoverPanel
                anchor={{ to: 'bottom start', gap: 6 }}
                className="z-50 w-72 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-xl"
            >
                {({ close }) => (
                    <div className="space-y-3">
                        <p className="truncate text-sm font-semibold text-gray-900">{linkedFixedAsset.name}</p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => { close(); onPreview(); }}
                                className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50"
                            >
                                Bekijken
                            </button>
                            <button
                                type="button"
                                onClick={() => { close(); onUnlink(); }}
                                disabled={hasUnsavedChanges}
                                className="flex-1 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Ontkoppelen…
                            </button>
                        </div>
                        {hasUnsavedChanges && <p className="text-xs text-gray-500">{UNSAVED_CHANGES_HINT}</p>}
                    </div>
                )}
            </PopoverPanel>
        </Popover>
    );
}
